import React, { FC, useState, useRef, useCallback, useMemo, useEffect, KeyboardEvent } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import * as Styled from './MTabGroup.styles';
import { MTabGroupProps, MTabGroupAlignment, type MTabGroupTab } from './MTabGroup.types';
import { MTabItem } from '../MTabItem';
import { MTabControlAlignment } from '../MTabControl/MTabControl.types';
import { MSlot } from '../MSlot';
import { MControlTabScroll } from '../MControlTabScroll';
import { MControlTabScrollDirection } from '../MControlTabScroll/MControlTabScroll.types';
import { useDirection } from '../../hooks';
import 'swiper/css';
import 'swiper/css/free-mode';

const COMPONENT_NAME = 'm-web-ui-kit-MTabGroup';
const TEST_ID = 'web-ui-kit-MTabGroup';

// Hoisted so the array reference is stable across renders
const SWIPER_MODULES = [FreeMode];
const MIN_VISIBLE_TABS = 2;
const MAX_VISIBLE_TABS = 10;

function clampActiveIndex(index: number | undefined, count: number): number {
  if (count === 0) return 0;
  if (index === undefined || Number.isNaN(index) || index < 0 || index >= count) return 0;
  return index;
}

function toControlAlignment(alignment: MTabGroupAlignment): MTabControlAlignment {
  return alignment === MTabGroupAlignment.Centered ? MTabControlAlignment.Center : MTabControlAlignment.Left;
}

function hasTabPanel(tab: MTabGroupTab): boolean {
  const hasMSlot = tab.mSlot?.cqItems && Object.keys(tab.mSlot.cqItems).length > 0;
  return Boolean(hasMSlot || tab.mSlot?.isAuthorMode || tab.content);
}

export const MTabGroup: FC<MTabGroupProps> = ({
  alignment = MTabGroupAlignment.Left,
  icon = false,
  description = false,
  tabs = [],
  className = '',
  initialActiveIndex,
  activeIndex,
  onTabChange,
}) => {
  const visibleTabs = useMemo(() => tabs.slice(0, Math.min(tabs.length, MAX_VISIBLE_TABS)), [tabs]);
  const [uncontrolledActiveIndex, setUncontrolledActiveIndex] = useState(() =>
    clampActiveIndex(initialActiveIndex, visibleTabs.length)
  );
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(true);
  const swiperRef = useRef<SwiperType | null>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  const { direction, isRTL } = useDirection();

  const controlAlignment = useMemo(() => toControlAlignment(alignment), [alignment]);
  const activeTabIndex =
    activeIndex === undefined ? uncontrolledActiveIndex : clampActiveIndex(activeIndex, visibleTabs.length);

  const handleTabClick = useCallback(
    (index: number) => {
      if (activeIndex === undefined) {
        setUncontrolledActiveIndex(index);
      }
      onTabChange?.(index);
    },
    [activeIndex, onTabChange]
  );

  const focusTab = useCallback((index: number) => {
    const button = tabRefs.current[index]?.querySelector<HTMLButtonElement>('[role="tab"]');
    button?.focus();
  }, []);

  const handleTabKeyDown = useCallback(
    (e: KeyboardEvent, index: number) => {
      const count = visibleTabs.length;
      let targetIndex: number | null = null;

      // In RTL, visual "right" means lower index and visual "left" means higher index,
      // matching the WAI-ARIA Authoring Practices for tabs in RTL.
      const nextKey = isRTL ? 'ArrowLeft' : 'ArrowRight';
      const prevKey = isRTL ? 'ArrowRight' : 'ArrowLeft';

      switch (e.key) {
        case nextKey:
          e.preventDefault();
          targetIndex = (index + 1) % count;
          break;
        case prevKey:
          e.preventDefault();
          targetIndex = (index - 1 + count) % count;
          break;
        case 'Home':
          e.preventDefault();
          targetIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          targetIndex = count - 1;
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          handleTabClick(index);
          return;
        default:
          return;
      }

      if (targetIndex !== null) {
        focusTab(targetIndex);
      }
    },
    [visibleTabs.length, handleTabClick, focusTab, isRTL]
  );

  const updateNavigationState = useCallback((swiper: SwiperType) => {
    if (!swiper) return;
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);

  const handlePrevClick = useCallback(() => {
    swiperRef.current?.slidePrev(300);
  }, []);

  const handleNextClick = useCallback(() => {
    swiperRef.current?.slideNext(300);
  }, []);

  const tabRefCallbacks = useRef<Map<number, (el: HTMLDivElement | null) => void>>(new Map());
  const getTabRef = useCallback((index: number) => {
    let cb = tabRefCallbacks.current.get(index);
    if (!cb) {
      cb = (el: HTMLDivElement | null) => {
        tabRefs.current[index] = el;
      };
      tabRefCallbacks.current.set(index, cb);
    }
    return cb;
  }, []);

  const onSwiper = useCallback(
    (swiper: SwiperType) => {
      swiperRef.current = swiper;
      swiper.slideTo(activeTabIndex, 0);
      updateNavigationState(swiper);
    },
    [activeTabIndex, updateNavigationState]
  );

  useEffect(() => {
    swiperRef.current?.slideTo(activeTabIndex, 300);
  }, [activeTabIndex]);

  // Start-side control scrolls toward the Swiper beginning; end-side scrolls toward the end.
  // CSS logical properties + MControlTabScroll's own RTL styles handle physical mirroring.
  const showStartControl = !isBeginning;
  const showEndControl = !isEnd;

  if (visibleTabs.length < MIN_VISIBLE_TABS) {
    return null;
  }

  return (
    <Styled.Wrapper data-component-name={COMPONENT_NAME} data-testid={TEST_ID} className={`m-tab-group ${className}`}>
      <Styled.TabContainer role="tablist" aria-label="Tabs" $alignment={alignment}>
        {showStartControl && (
          <Styled.ScrollControl $side="start">
            <MControlTabScroll direction={MControlTabScrollDirection.Left} onClick={handlePrevClick} tabIndex={-1} />
          </Styled.ScrollControl>
        )}

        <Styled.SwiperWrapper $alignment={alignment}>
          <Swiper
            key={direction}
            modules={SWIPER_MODULES}
            slidesPerView="auto"
            freeMode={{
              enabled: true,
              momentum: true,
              momentumRatio: 0.5,
            }}
            dir={direction}
            onSwiper={onSwiper}
            onSlideChange={updateNavigationState}
            onReachBeginning={updateNavigationState}
            onReachEnd={updateNavigationState}
            onFromEdge={updateNavigationState}
            onProgress={updateNavigationState}
            watchOverflow={true}
            observer={true}
            observeParents={true}
          >
            {visibleTabs.map((tab, index) => {
              const panelId = `m-tab-group-${index}-panel`;
              return (
                <SwiperSlide key={index}>
                  <MTabItem
                    id={`m-tab-group-${index}`}
                    ref={getTabRef(index)}
                    active={index === activeTabIndex}
                    showIcon={icon}
                    description={description ? tab.description : undefined}
                    aIcon={tab.aIcon}
                    alignment={controlAlignment}
                    label={tab.label}
                    tabIndex={index === activeTabIndex ? 0 : -1}
                    aria-controls={hasTabPanel(tab) ? panelId : undefined}
                    onTabClick={() => handleTabClick(index)}
                    onTabKeyDown={e => handleTabKeyDown(e, index)}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </Styled.SwiperWrapper>

        {showEndControl && (
          <Styled.ScrollControl $side="end">
            <MControlTabScroll direction={MControlTabScrollDirection.Right} onClick={handleNextClick} tabIndex={-1} />
          </Styled.ScrollControl>
        )}
      </Styled.TabContainer>

      {visibleTabs.map((tab, index) => {
        const isActive = index === activeTabIndex;
        const panelId = `m-tab-group-${index}-panel`;
        const tabId = `m-tab-group-${index}-tab`;
        const hasMSlot = tab.mSlot?.cqItems && Object.keys(tab.mSlot.cqItems).length > 0;
        const hasContent = hasTabPanel(tab);

        if (!hasContent) return null;

        return (
          <Styled.PanelArea
            key={index}
            $hidden={!isActive}
            $alignment={alignment}
            role="tabpanel"
            id={panelId}
            aria-labelledby={tabId}
            aria-hidden={!isActive}
          >
            {hasMSlot || tab.mSlot?.isAuthorMode ? (
              <MSlot
                pagePath={tab.mSlot?.pagePath ?? ''}
                itemPath={tab.mSlot?.itemPath ?? ''}
                isAuthorMode={tab.mSlot?.isAuthorMode}
                cqItems={tab.mSlot?.cqItems}
                aemReactComponentMapping={tab.mSlot?.aemReactComponentMapping ?? {}}
                className={tab.mSlot?.className}
                containerClassName={tab.mSlot?.containerClassName}
                model={tab.mSlot?.model}
              />
            ) : (
              tab.content
            )}
          </Styled.PanelArea>
        );
      })}
    </Styled.Wrapper>
  );
};

export { TEST_ID as M_TAB_GROUP_TEST_ID };
